import { useModal } from '@ebay/nice-modal-react'
import { Link, useNavigate } from '@tanstack/router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from '@linaria/react'

import coin_anim from '../../assets/common/coin_anim.gif'
import useGeoCheck from '../../hooks/useGeoCheck'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import { api } from '../../utils/api-client'
import { numberWithCommas } from '../../utils/number'
import Icon from '../icon'
import Sidebar from '../sidebar'
import LoginModal from './log-in-modal'
import useRefreshBalance from '../../hooks/useRefreshBalance'
import WalletManagementModal from '../wallet-management/wallet-management-modal'
import LanguageSelector from './language-selector'

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 20;
  background-color: var(--primary);
  padding: 10px 10px 10px 0;
  height: 60px;
  transition: all 0.2s ease-in-out;
  width: 100%;
`

const ShowProfile = styled.button`
  padding: 20px;
  display: flex;
  svg {
    pointer-events: none;
    * {
      pointer-events: none;
    }
  }
`

const WalletContainer = styled.div`
  margin-right: 8px;
  position: relative;
  svg {
    fill: var(--txt);
  }
`

const Coins = styled.img`
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  position: absolute;
  top: -5px;
  left: -60px;
  width: 150px;
  animation: fadeIn 0.2s ease-in-out;
  pointer-events: none;
`

const Balance = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: var(--txt);
  margin-right: 8px;
`

const Btn = styled.span`
  height: 35px;
  background-color: var(--primary-btn-bg);
  border-radius: 6px;
  padding: 0 12px;
  p {
    font-size: 12px;
    font-family: 'Poppins';
    font-weight: 300;
    color: var(--primary-btn-txt);
    text-transform: uppercase;
  }
  &.login {
    background-color: var(--highlight);
    margin-right: 8px;
  }
`

const Number = styled.p`
  position: absolute;
  top: 17px;
  left: 35px;
  width: 7px;
  height: 7px;
  border-radius: 6px;
  background-color: #eb4243;
`

const Refresh = styled.button`
  display: flex;
  @keyframes rotate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  &.rotate {
    animation: rotate 1.5s linear infinite;
  }
`

interface HeaderProps {
  hideBalance?: boolean
}

const Header = ({ hideBalance }: HeaderProps) => {
  const { geoCheck } = useGeoCheck()
  const navigate = useNavigate()
  const isCoinAnimVisible = useBoundStore(
    state => state.common.isCoinAnimVisible,
  )
  const unified_balance = useBoundStore(state => state.user.unified_balance)
  const username = useBoundStore(state => state.user.username)
  const isFullScreen = useBoundStore(state => state.common.isFullScreen)
  const logInModal = useModal(LoginModal)
  const sidebarDrawerModal = useModal(Sidebar)
  const walletManagement = useModal(WalletManagementModal)
  const { t } = useTranslation('common')
  const { refreshBalance, isLoading: balanceLoading } = useRefreshBalance()
  const [noOfUnread, setNoOfUnread] = useState(0)

  useEffect(() => {
    if (!username) return

    const fetchNumberOfUnreadMessages = async () => {
      const response = await api.post<ApiResponse<string>>('/ajax_data.php', {
        type: 'noread_message',
      })
      const { status, info } = response.data
      if (status === 1) {
        setNoOfUnread(parseInt(info))
      }
    }
    fetchNumberOfUnreadMessages()
  }, [username])

  useEffect(() => {
    if (!username) return
    refreshBalance()
  }, [username])

  return (
    <Container className="flx-btw-ctr" style={{ top: isFullScreen ? -60 : 0 }}>
      <div className="flx-ctr">
        <div style={{ position: 'relative', display: 'flex' }}>
          {noOfUnread > 0 && <Number className="flx-ctr" />}
          <ShowProfile onClick={() => sidebarDrawerModal.show()}>
            <Icon width={19} height={19} id="hamburger" />
          </ShowProfile>
        </div>
        <Link to="/" className="flx-ctr">
          <img
            src={`https://sfgdefe0923.fun88adrods.online/mexicoimages/logo/dark.webp`}
            alt="Company Logo"
            style={{ width: '78px', aspectRatio: '471 / 104' }}
          />
        </Link>
      </div>
      <div className="flx-ctr">
        {username ? (
          <>
            <button
              className="flx-ctr"
              onClick={() => {
                walletManagement.show({
                  walletManagementDefaultTab: { page: 0, tab: 0 },
                })
              }}>
              <WalletContainer>
                <Icon id="wallet" width="22" height="23" />
                {isCoinAnimVisible && <Coins src={coin_anim} />}
              </WalletContainer>
              {!hideBalance && (
                <Balance>
                  ${numberWithCommas(parseFloat(unified_balance))}
                </Balance>
              )}
            </button>
            {!hideBalance && (
              <Refresh
                onClick={refreshBalance}
                className={balanceLoading ? 'rotate' : ''}>
                <Icon
                  id="refresh-balance"
                  width="17"
                  height="17"
                  fill="#D2D4D6"
                />
              </Refresh>
            )}
          </>
        ) : (
          <>
            <Btn
              className="flx-ctr login"
              style={{ marginRight: '10px' }}
              onClick={() => geoCheck(logInModal.show)}>
              <p>{t('login')}</p>
            </Btn>
            <Btn
              className="flx-ctr"
              onClick={() =>
                geoCheck(() =>
                  navigate({
                    to: '/register'
                  }),
                )
              }>
              <p>{t('register')}</p>
            </Btn>
            <LanguageSelector />
          </>
        )}
      </div>
    </Container>
  )
}

export default Header
